import { useCallback, useState } from 'react'

/**
 * Custom hook for managing toast notifications.
 *
 * This hook provides centralized state management for toast notifications,
 * eliminating the need to duplicate toast state across components.
 *
 * @returns {Object} Toast state and handlers
 */
export const useToast = () => {
  const [toast, setToast] = useState({
    open: false,
    message: '',
    type: 'info',
    duration: 3000
  })

  /**
   * Shows a toast notification with the specified parameters.
   *
   * @param {string} message - The message to display
   * @param {string} [type='info'] - The type of toast (success, error, info, warning)
   * @param {number} [duration=3000] - Duration in milliseconds before auto-hide
   */
  const showToast = useCallback(
    (message: string, type = 'info', duration = 3000) => {
      setToast({
        open: true,
        message,
        type,
        duration
      })
    },
    []
  )

  /**
   * Hides the current toast notification.
   */
  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }))
  }, [])

  /**
   * Shows a success toast with default styling.
   *
   * @param {string} message - The success message
   * @param {number} [duration=4000] - Duration in milliseconds
   */
  const showSuccess = useCallback(
    (message: string, duration = 4000) => {
      showToast(message, 'success', duration)
    },
    [showToast]
  )

  /**
   * Shows an error toast with default styling.
   *
   * @param {string} message - The error message
   * @param {number} [duration=5000] - Duration in milliseconds
   */
  const showError = useCallback(
    (message: string, duration = 5000) => {
      showToast(message, 'error', duration)
    },
    [showToast]
  )

  /**
   * Shows a warning toast with default styling.
   *
   * @param {string} message - The warning message
   * @param {number} [duration=4000] - Duration in milliseconds
   */
  const showWarning = useCallback(
    (message: string, duration = 4000) => {
      showToast(message, 'warning', duration)
    },
    [showToast]
  )

  /**
   * Shows an info toast with default styling.
   *
   * @param {string} message - The info message
   * @param {number} [duration=3000] - Duration in milliseconds
   */
  const showInfo = useCallback(
    (message: string, duration = 3000) => {
      showToast(message, 'info', duration)
    },
    [showToast]
  )

  return {
    // State
    toast,

    // Handlers
    showToast,
    hideToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }
}
